import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Loader, X } from 'lucide-react';
import useApi from '../../utils/useApi';
import styles from './Notifications.module.css';
import {useNavigate} from "react-router-dom";

const NotificationItem = ({ notification }) => {
    const { trackProductView } = useApi();
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const navigate = useNavigate();


    const handleNotificationClick = async (e) => {
        e.preventDefault();
        if (notification.product.product_deal && notification.product.product_deal.length > 0) {
            setIsBottomSheetOpen(true);
            try {
                await trackProductView(notification.product.id,notification.dispensary.id);
            }
            catch (error) {
                console.error("Failed to track product view:", error);
            }
        } else {
            navigate(`/business/${notification.dispensary.id}`);
        }
    };

    const closeBottomSheet = () => {
        setIsBottomSheetOpen(false);
    };

    const getIcon = () => {
        return <div className={styles.avatar} style={{backgroundImage: `url(${notification.product.formatted_thumbnail_url})`}}>

        </div>;
    };

    const getTimeAgo = (date) => {
        const hours = Math.abs(new Date() - new Date(date)) / 36e5;
        if(hours < 1){
            return "Just Now";
        }
        else if (hours < 24) {
            return `${Math.round(hours)}h ago`;
        }
        return new Date(date).toLocaleDateString(undefined, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className={styles.notificationItem} onClick={handleNotificationClick}>

            <div className={styles.avatarWrap}>
                {getIcon()}

            </div>
            <div className={styles.notificationContent}>
                <div className={styles.notificationText}>
                    <span className={styles.notificationTitle}>{notification.title}</span>{' '}
                    {notification.message}
                </div>
                <div className={styles.notificationMeta}>
                    {notification.type && (
                        <span className={styles.notificationTag}>
                            {notification.type.replace('_', ' ')}
                        </span>
                    )}
                    <span className={styles.notificationTime}>
                        {getTimeAgo(notification.created_at)}
                    </span>
                </div>
            </div>
        </div>
    );
};

const Notifications = ({ handleShowHeader, handleShowSettingsHeader }) => {
    const { getNotifications } = useApi();
    const [notifications, setNotifications] = useState({});
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const groupNotificationsByDate = (notificationsList) => {
        const grouped = notificationsList.reduce((acc, notification) => {
            const date = new Date(notification.created_at);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let dateKey;
            if (date.toDateString() === today.toDateString()) {
                dateKey = 'TODAY';
            } else if (date.toDateString() === yesterday.toDateString()) {
                dateKey = 'YESTERDAY';
            } else {
                dateKey = date.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }

            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(notification);
            return acc;
        }, {});

        return grouped;
    };

    const loadNotifications = useCallback(async () => {
        if (isLoading) return;

        console.log(page);
        try {
            setIsLoading(true);
            setError(null);
            const response = await getNotifications(page);

            if (response.data) {
                setNotifications(prev => {
                    const newNotifications = [...Object.values(prev).flat(), ...response.data];
                    return groupNotificationsByDate(newNotifications);
                });
                setHasMore(response.next_page_url !== null);
            }
        } catch (err) {
            setError('Failed to load notifications. Please try again.');
        } finally {
            setIsLoading(false);
            setInitialLoadComplete(true);
        }
    }, [page, getNotifications]);

    // Initial load
    useEffect(() => {
        loadNotifications();
    }, []); // Only run on mount

    // Handle header visibility
    useEffect(() => {
        handleShowHeader(false);
        handleShowSettingsHeader(true);
    }, [handleShowHeader, handleShowSettingsHeader]);

    // Load more when page changes
    useEffect(() => {
        if (initialLoadComplete && page > 1) {
            loadNotifications();
        }
    }, [page, loadNotifications, initialLoadComplete]);

    // Handle scroll
    const handleScroll = useCallback(() => {
        if (!hasMore || isLoading || !initialLoadComplete) return;

        const distanceFromBottom =
            document.documentElement.scrollHeight -
            (window.innerHeight + document.documentElement.scrollTop);

        if (distanceFromBottom <= 1000) {
            setPage(prev => prev + 1);
        }
    }, [hasMore, isLoading, initialLoadComplete]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerTop}>
                    <h2 className={styles.title}>Notifications</h2>
                    <button className={styles.markRead}>
                        Mark all as read
                    </button>
                </div>
            </div>

            <div className={styles.notificationsContainer}>
                {error && <div className={styles.error}>{error}</div>}

                {Object.entries(notifications).map(([date, notifs]) => (
                    <div key={date}>
                        <div className={styles.dateHeader}>
                            {date}
                        </div>
                        <div className={styles.notificationsList}>
                            {notifs.map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className={styles.loader}>
                        <Loader className={styles.loaderIcon} />
                    </div>
                )}

                {!isLoading && Object.keys(notifications).length === 0 && (
                    <div className={styles.emptyState}>
                        <Bell className={styles.emptyIcon} />
                        <p className={styles.emptyText}>No notifications yet</p>
                    </div>
                )}

                {!hasMore && Object.keys(notifications).length > 0 && (
                    <div className={styles.noMore}>No more notifications</div>
                )}
            </div>
        </div>
    );
};

export default Notifications;
